import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import ExportCsvButton from "../components/ExportCsvButton";
import { CheckCircle2, XCircle, AlertOctagon, FileDown, Scan, ChevronDown, ChevronUp, Loader2, ShieldAlert, Sparkles, MessageSquare } from "lucide-react";
import { toast, Toaster } from "sonner";
import ClaimExtras, { SlaCountdown } from "../components/ClaimExtras";

const STATUS_BADGE = {
  flash_approved: "badge-flash", paid: "badge-paid",
  submitted: "badge-pending", validating: "badge-validating", manual_review: "badge-review",
  rejected: "badge-rejected", escalated: "badge-suspended", closed: "badge-cooling",
};
const RISK_BADGE = { low: "badge-low", medium: "badge-medium", high: "badge-high" };

function OcrBadge({ screening }) {
  if (!screening || !screening.results?.length) {
    return <span className="text-[10px] text-[#5c6360] font-mono uppercase tracking-wider">no ocr</span>;
  }
  const okResults = screening.results.filter((r) => r.status === "ok");
  const signals = screening.extra_fraud_signals || [];
  const avgConf = okResults.length
    ? Math.round(okResults.reduce((s, r) => s + (r.confidence || 0), 0) / okResults.length)
    : 0;
  const tone = signals.length > 0
    ? { cls: "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/30", label: "Flagged" }
    : avgConf >= 80
    ? { cls: "bg-[#114232]/8 text-[#114232] border-[#114232]/25", label: "Clean" }
    : { cls: "bg-[#d49a36]/15 text-[#8a5a16] border-[#d49a36]/35", label: "Partial" };
  const topSignal = signals[0] || null;
  const tooltip = signals.length > 0
    ? `Top signal: ${topSignal}${signals.length > 1 ? ` (+${signals.length - 1} more)` : ""}`
    : `Avg confidence ${avgConf}% across ${okResults.length} file${okResults.length === 1 ? "" : "s"}`;
  return (
    <span
      title={tooltip}
      className={`relative group inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider cursor-help ${tone.cls}`}
    >
      <Scan className="w-3 h-3" /> {tone.label} · {avgConf}%
      {topSignal && (
        <span className="hidden group-hover:flex absolute top-full left-0 mt-1 z-20 w-60 p-2 rounded-md bg-[#1b1d1c] text-white text-[10px] font-normal normal-case tracking-normal shadow-lg pointer-events-none">
          <span><span className="font-semibold uppercase tracking-wider">Top fraud signal:</span><br />{topSignal}</span>
        </span>
      )}
    </span>
  );
}

function OcrDetailPanel({ screening, claimId }) {
  const [explainBusy, setExplainBusy] = React.useState(false);
  const [explanation, setExplanation] = React.useState(null);

  async function explain() {
    setExplainBusy(true);
    try {
      const { data: r } = await api.post(`/admin/claims/${claimId}/explain`);
      setExplanation(r.explanation);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setExplainBusy(false);
    }
  }

  if (!screening || !screening.results?.length) {
    return (
      <div className="p-4 text-xs text-[#5c6360] flex items-center justify-between gap-3 flex-wrap" data-testid={`ocr-empty-${claimId}`}>
        <div>OCR has not run yet on this claim. Click <span className="font-semibold">Run OCR</span> to start.</div>
        <button
          type="button"
          onClick={explain}
          disabled={explainBusy}
          data-testid={`claim-explain-${claimId}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#114232]/15 bg-white hover:bg-[#114232]/5 text-[#114232] text-[11px] uppercase tracking-wider font-semibold"
        >
          {explainBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Explain
        </button>
        {explanation && (
          <div className="w-full mt-3 bg-[#114232]/5 border border-[#114232]/15 rounded-md p-3 text-[12px] text-[#1b1d1c] whitespace-pre-wrap" data-testid={`claim-explanation-${claimId}`}>
            <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-[0.18em] text-[#c15337] font-bold">
              <MessageSquare className="w-3 h-3" /> Coach explanation
            </div>
            {explanation}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="p-4 bg-[#114232]/3 border-t border-[#114232]/10 space-y-3" data-testid="ocr-detail-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">
          OCR results · {new Date(screening.completed_at).toLocaleString()}
        </div>
        <button
          type="button"
          onClick={explain}
          disabled={explainBusy}
          data-testid={`claim-explain-${claimId}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#114232]/15 bg-white hover:bg-[#114232]/5 text-[#114232] text-[11px] uppercase tracking-wider font-semibold"
        >
          {explainBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Why was this flagged?
        </button>
      </div>
      {explanation && (
        <div className="bg-[#114232]/5 border border-[#114232]/15 rounded-md p-3 text-[12px] text-[#1b1d1c] whitespace-pre-wrap" data-testid={`claim-explanation-${claimId}`}>
          <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-[0.18em] text-[#c15337] font-bold">
            <MessageSquare className="w-3 h-3" /> Coach explanation
          </div>
          {explanation}
        </div>
      )}
      {(screening.extra_fraud_signals || []).length > 0 && (
        <div className="bg-[#c15337]/8 border border-[#c15337]/20 rounded-md px-3 py-2 text-xs text-[#8b3a23] flex items-start gap-2">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold uppercase tracking-wider text-[10px] mb-1">Fraud signals</div>
            {screening.extra_fraud_signals.map((s, i) => (<div key={i}>• {s}</div>))}
          </div>
        </div>
      )}
      <div className="space-y-2">
        {screening.results.map((r) => (
          <div key={r.file_id} className="bg-white border border-[#114232]/10 rounded-md p-3" data-testid={`ocr-result-${r.file_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-mono text-xs text-[#114232]">{r.original_name || r.file_id}</div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                <span className="text-[#5c6360]">{r.document_type || "—"}</span>
                <span className={`px-1.5 py-0.5 rounded font-semibold ${
                  r.status === "ok" ? "bg-[#114232]/8 text-[#114232]" :
                  r.status === "skipped" ? "bg-[#d49a36]/12 text-[#8a5a16]" :
                  "bg-[#c15337]/12 text-[#8b3a23]"
                }`}>{r.status}</span>
                {r.confidence != null && (
                  <span className="font-mono text-[#5c6360]">{r.confidence}%</span>
                )}
              </div>
            </div>
            {r.suspected_amount_kes != null && (
              <div className="mt-1.5 text-xs">
                Suspected amount: <span className="font-mono font-semibold">KES {Number(r.suspected_amount_kes).toLocaleString()}</span>
              </div>
            )}
            {r.fraud_signals?.length > 0 && (
              <div className="mt-1.5 text-xs text-[#8b3a23]">
                Signals: {r.fraud_signals.join(", ")}
              </div>
            )}
            {r.extracted_text && (
              <details className="mt-2 text-[11px] text-[#5c6360]">
                <summary className="cursor-pointer font-semibold uppercase tracking-wider text-[10px]">Extracted text</summary>
                <pre className="mt-1.5 p-2 bg-[#114232]/5 rounded whitespace-pre-wrap font-mono text-[10px] max-h-32 overflow-auto">
                  {r.extracted_text}
                </pre>
              </details>
            )}
            {r.error && (
              <div className="mt-1.5 text-[11px] text-[#8b3a23]">
                OCR unavailable for this file.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Claims() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [ocrBusyId, setOcrBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/claims", { params: filter ? { status: filter } : {} })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.claims || []);
        setItems(list);
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function decide(claim, action) {
    try {
      await api.patch(`/claims/${claim.id}/${action}`, { notes: `${action} via admin portal` });
      toast.success(`Claim ${claim.claim_number} ${action}ed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadDecisionLetter(claim) {
    try {
      const { data: blob } = await api.get(
        `/pdfs/claims/${claim.id}/decision-letter`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `claim_decision_${claim.claim_number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function runOcr(claim) {
    setOcrBusyId(claim.id);
    try {
      const { data: r } = await api.post(`/claims/${claim.id}/ocr-prescreen`);
      if (r.ok) {
        toast.success(`OCR completed for ${claim.claim_number}`);
      } else {
        toast.message("OCR not run", {
          description: r.error === "no_evidence_files"
            ? "This claim has no attached evidence files."
            : (r.error || "Unable to run OCR right now."),
        });
      }
      // Reload to pick up the new ocr_screening blob
      load();
      // Auto-expand to surface the panel
      setExpandedId(claim.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setOcrBusyId(null);
    }
  }

  return (
    <div className="p-8 space-y-6" data-testid="claims-page">
      <Toaster position="top-right" />
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">{t("admin_pages.claims_section")}</div>
          <h1 className="font-heading text-3xl font-bold mt-1 text-[#114232]">{t("admin_pages.claims_title")}</h1>
        </div>
        <ExportCsvButton
          path="/admin/exports/claims.csv"
          filename="claims"
          params={filter ? { status: filter } : {}}
          testid="export-claims-csv"
        />
      </header>

      <div className="flex items-center gap-2 text-sm flex-wrap">
        {["", "submitted", "validating", "flash_approved", "manual_review", "paid", "rejected", "escalated"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`claims-filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-sm border capitalize ${
              filter === s ? "bg-primary text-primary-foreground border-primary"
                           : "border-border hover:bg-stone-50"
            }`}
          >
            {s ? s.replace(/_/g, " ") : "All"}
          </button>
        ))}
      </div>

      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="claims-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">Claim #</th>
              <th className="text-left font-medium py-3 px-5">Type</th>
              <th className="text-left font-medium py-3 px-5">Amount</th>
              <th className="text-left font-medium py-3 px-5">Country</th>
              <th className="text-left font-medium py-3 px-5">Fraud</th>
              <th className="text-left font-medium py-3 px-5">OCR</th>
              <th className="text-left font-medium py-3 px-5">Status</th>
              <th className="text-right font-medium py-3 px-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const expanded = expandedId === c.id;
              return (
              <React.Fragment key={c.id}>
              <tr className="border-t border-border table-row-hover" data-testid={`claim-row-${c.id}`}>
                <td className="py-3 px-5 font-mono text-xs">{c.claim_number}</td>
                <td className="py-3 px-5 capitalize">{c.claim_type.replace(/_/g, " ")}</td>
                <td className="py-3 px-5 font-mono">KES {c.amount_claimed.toLocaleString()}</td>
                <td className="py-3 px-5">
                  <span className={c.country !== "KE" ? "text-destructive font-semibold" : ""}>{c.country}</span>
                </td>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{c.fraud_score}</span>
                    <span className={`badge-status ${RISK_BADGE[c.risk_level] || "badge-medium"}`}>{c.risk_level}</span>
                  </div>
                </td>
                <td className="py-3 px-5" data-testid={`claim-ocr-badge-${c.id}`}>
                  <OcrBadge screening={c.ocr_screening} />
                </td>
                <td className="py-3 px-5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`badge-status ${STATUS_BADGE[c.status] || "badge-pending"}`}>
                      {c.status.replace(/_/g, " ")}
                    </span>
                    {c.sla_due_at && <SlaCountdown dueAt={c.sla_due_at} />}
                  </div>
                </td>
                <td className="py-3 px-5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" data-testid={`approve-${c.id}`} onClick={() => decide(c, "approve")}
                            disabled={["paid", "rejected", "closed"].includes(c.status)}>
                      <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`reject-${c.id}`} onClick={() => decide(c, "reject")}
                            disabled={["paid", "rejected", "closed"].includes(c.status)}>
                      <XCircle className="w-4 h-4 text-red-700" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`escalate-${c.id}`} onClick={() => decide(c, "escalate")}>
                      <AlertOctagon className="w-4 h-4 text-amber-600" />
                    </Button>
                    <Button size="sm" variant="ghost"
                            data-testid={`run-ocr-${c.id}`}
                            onClick={() => runOcr(c)}
                            disabled={ocrBusyId === c.id}
                            title="Run OCR pre-screening">
                      {ocrBusyId === c.id
                        ? <Loader2 className="w-4 h-4 text-[#114232] animate-spin" />
                        : <Scan className="w-4 h-4 text-[#114232]" />}
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`decision-letter-${c.id}`}
                            onClick={() => downloadDecisionLetter(c)}
                            disabled={["submitted", "validating", "manual_review"].includes(c.status)}
                            title="Download decision letter PDF">
                      <FileDown className="w-4 h-4 text-[#114232]" />
                    </Button>
                    <Button size="sm" variant="ghost"
                            data-testid={`expand-claim-${c.id}`}
                            onClick={() => setExpandedId(expanded ? null : c.id)}
                            title="Show OCR detail">
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </td>
              </tr>
              {expanded && (
                <tr key={`${c.id}-detail`} className="border-t border-border" data-testid={`claim-detail-${c.id}`}>
                  <td colSpan={8} className="p-0">
                    <OcrDetailPanel screening={c.ocr_screening} claimId={c.id} />
                    <ClaimExtras claim={c} onAssignmentChanged={load} />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ); })}
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No claims found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
